import { AgGridReact } from "ag-grid-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, Col, Row } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { setNextPrevIds } from "../../../containers/next_prev_redux/Actions";
import { instance } from "../../utilites/ApiClient";
import "./TableStyle.scss";
import SidebarFilters from "./SidebarFilter/SidebarFilters";

import {
  actionValueRenderer,
  getValueFormatters,
  getFilterParams,
  getComparators,
  getFloatingFilterComponents,
  checkboxRenderer,
  iconRenderer,
  hyperlinkRenderer,
  onColumnVisible,
} from "./tableFunctions";
import PriorityRenderer from "./PriorityRenderer";

//An object containing valueFormatters. Different formatting for diferent column types
let valueFormatters = getValueFormatters();
//an object containing filterParams. Different filterParams depending on the column type
let filterParams = getFilterParams();
let comparators = getComparators();
let floatingFilterComponents = getFloatingFilterComponents();

export default function ServerSideListView({
  columnNames,
  rowData = [],
  rowClick,
  exportTitle,
  clickAdd,
  clickEdit,
  clickDelete,
  shouldSetNextPrevIds = false,
  filterCallback,
  filterString,
  sort,
  exportUrl,
  tableHeight = "70vh",
  extraFilterButton,
  onSaveFilter,
  filterType,
  externalFilter,
  loading
}) {
  console.log("ServerSideListView render", { rowData });
  const [hiddenColumns, setHiddenColumns] = useState(() => {
    let hiddenColumns = [];
    for (let col of columnNames) {
      if (col.hide) {
        hiddenColumns.push(col.field);
      }
    }
    return hiddenColumns;
  });
  const [exportEnabled, setExportEnabled] = useState(false);
  const location = useLocation();
  const dispatch = useDispatch();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  let columnDefs = useMemo(() => {
    const cols = createColumnDefs(columnNames, sort, clickAdd, clickEdit, clickDelete);
    return isMobile ? cols.map(col => ({ ...col, pinned: undefined })) : cols;
  }, [columnNames, sort, isMobile]);
  const [api, setApi] = useState(null);
  const [columnApi, setColumnApi] = useState(null);

  let [selectedIds, setSelectedIds] = useState([]);

  const [filters, setFilters] = useState({}); // this is only used to pass to the sidebar

  const gridRef = useRef(null);
  const timeoutsRef = useRef([]);

  // Memoize rowData with row_id to prevent unnecessary re-renders
  const memoizedRowData = useMemo(() => {
    return rowData.map((row, i) => ({ ...row, row_id: i }));
  }, [rowData]);

  // useEffect(() => {
  //   if (!api) return;

  //   safeGridCall(() => {
  //     let i = 0;
  //     const newData = rowData.map((row) => ({
  //       ...row,
  //       row_id: i++,
  //     }));

  //     api.setRowData(newData);
  //   });
  // }, [rowData, api]);

  useEffect(() => {
    if (!externalFilter || !api || !columnApi) return;

    // Wrap API calls in setTimeout to prevent render cycle issues
    const timeoutId = setTimeout(() => {
      safeGridCall(() => {
        try {
          const hasFilter =
            externalFilter.filterModel &&
            Object.keys(externalFilter.filterModel).length > 0;

          const hasSort =
            Array.isArray(externalFilter.sortModel) &&
            externalFilter.sortModel.length > 0;

          if (!hasFilter && !hasSort) {
            filterCallback &&
              filterCallback({
                filter: {},
                sort: null,
                pageSize: externalFilter?.pageSize || null,
                defaultFilter: externalFilter?.filter_name || null,
              });
          }
          api.setFilterModel(externalFilter.filterModel || null);

          columnApi.applyColumnState({
            defaultState: { sort: null, sortIndex: null },
          });
          if (externalFilter.sortModel?.length) {
            columnApi.applyColumnState({
              state: externalFilter.sortModel.map((s, i) => ({
                colId: s.colId,
                sort: s.sort,
                sortIndex: i,
              })),
            });
          }

          if (externalFilter.columnVisibility) {
            const visibility = externalFilter.columnVisibility;
            const newState = columnApi.getColumnState().map((col) => ({
              ...col,
              hide: visibility[col.colId] === false,
            }));
            columnApi.setColumnState(newState);

            const hidden = Object.entries(visibility)
              .filter(([, isVisible]) => isVisible === false)
              .map(([colId]) => colId);

            setHiddenColumns(hidden);
          }
        } catch (e) {
          console.warn("AG Grid restore skipped:", e);
        }
      });
    }, 0);

    timeoutsRef.current.push(timeoutId);

    return () => clearTimeout(timeoutId);
  }, [externalFilter, api, columnApi]);

  // Cleanup effect to clear all pending timeouts on unmount
  useEffect(() => {
    return () => {
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, []);

  // useEffect(() => {
  //   if (!externalFilter || !api || !columnApi) return;
  //   const gridApi = api;
  //   const colApi = columnApi;
  //   let frameId;
  //   const timeoutId = setTimeout(() => {
  //     frameId = window.requestAnimationFrame(() => {
  //       // grid might have been destroyed/unmounted in the meantime
  //       if (!gridApi || !colApi) return;
  //       // Saved / external filter selected
  //       const { filterModel, sortModel, columnVisibility, pageSize } =
  //         externalFilter;

  //       // Apply filter model from saved filter
  //       safeGridCall(() => {
  //         gridApi.setFilterModel(filterModel || null);
  //       });
  //       setFilters(filterModel || {});

  //       // Reset all existing sorting
  //       safeGridCall(() => {
  //         colApi.applyColumnState({
  //           defaultState: { sort: null, sortIndex: null },
  //         });
  //       });

  //       // Apply saved sort
  //       if (Array.isArray(sortModel) && sortModel.length > 0) {
  //         safeGridCall(() => {
  //           colApi.applyColumnState({
  //             state: sortModel.map((s, index) => ({
  //               colId: s.colId,
  //               sort: s.sort,
  //               sortIndex: index,
  //             })),
  //           });
  //         });
  //       }

  //       // Apply column visibility & sync sidebar
  //       if (columnVisibility && Object.keys(columnVisibility).length > 0) {
  //         safeGridCall(() => {
  //           // Get current column state safely
  //           const currentState = colApi.getColumnState
  //             ? colApi.getColumnState()
  //             : [];

  //           // Build new visibility state
  //           const newState = (currentState || []).map((col) => {
  //             const { sort, sortIndex, ...rest } = col;

  //             if (
  //               Object.prototype.hasOwnProperty.call(
  //                 columnVisibility,
  //                 col.colId
  //               )
  //             ) {
  //               return {
  //                 ...rest,
  //                 hide: columnVisibility[col.colId] === false ? false : true,
  //               };
  //             }

  //             return rest;
  //           });

  //           // Apply the new column visibility state safely
  //           if (newState.length > 0) {
  //             colApi.setColumnState(newState);
  //           }

  //           // Now update your React state normally (safe)
  //           const hidden = Object.entries(columnVisibility)
  //             .filter(([, visible]) => !visible)
  //             .map(([colId]) => colId);

  //           setHiddenColumns(hidden);
  //         });
  //       }

  //       // Apply saved page size
  //       // if (pageSize) {
  //       //   gridApi.paginationSetPageSize(pageSize);
  //       // }
  //     });
  //   }, 0);

  //   return () => {
  //     clearTimeout(timeoutId);
  //     if (frameId) {
  //       window.cancelAnimationFrame(frameId);
  //     }
  //   };
  // }, [externalFilter, api, columnApi, setHiddenColumns]);

  function safeGridCall(apiFn) {
    setTimeout(() => {
      requestAnimationFrame(() => {
        try {
          apiFn();
        } catch (e) {
          console.warn("AG Grid prevented unsafe API call:", e);
        }
      });
    }, 0);
  }

  const clearFilter = () => {
    safeGridCall(() => api?.setFilterModel(null));
  };

  useEffect(() => {
    if (!api) return;

    safeGridCall(() => {
      if (loading) {
        api.showLoadingOverlay();
      } else {
        api.hideOverlay();
      }
    });
  }, [loading, api]);


  // const onGridReady = useCallback((event) => {
  //   setApi(event.api);
  //   setColumnApi(event.columnApi);
  //   event.api.setFilterModel(JSON.parse(filterString));
  // }, []);

  const onGridReady = useCallback((event) => {
    setApi(event.api);
    setColumnApi(event.columnApi);

    // Wrap in setTimeout to prevent render cycle issues
    const timeoutId = setTimeout(() => {
      safeGridCall(() => {
        const parsed =
          filterString && filterString !== "null"
            ? JSON.parse(filterString)
            : null;

        event.api.setFilterModel(parsed);
      });
    }, 0);

    timeoutsRef.current.push(timeoutId);
  }, []);

  // const onGridReady = useCallback((params) => {
  //     if (shouldSetNextPrevIds) {
  //         let ids = []
  //         params.api.forEachNodeAfterFilterAndSort((node) => {
  //             ids.push(node.data.id)
  //         })
  //         dispatch(setNextPrevIds(ids, exportTitle))
  //     }
  // }, []);
  const onFilterOrSortChanged = (event) => {
    const timeoutId = setTimeout(() => {
    if (shouldSetNextPrevIds) {
      let ids = [];
      event.api.forEachNodeAfterFilterAndSort((node) => {
        ids.push(node.data.id);
      });
      dispatch(setNextPrevIds(ids, exportTitle));
    }
    let currentFilters = event.api.getFilterModel();
    setFilters(currentFilters);
    // if (JSON.stringify(tableFilters[tableKey]) != JSON.stringify(currentFilters)) {
    //     dispatch(addTableFilter(tableKey, currentFilters))
    // }
    // let sortedColumn;
    // safeGridCall(() => {
    //   const colState = event.columnApi.getColumnState();
    //   sortedColumn = colState.filter((c) => c.sort)[0];
    // });

    const colState = event.columnApi.getColumnState();
    const sortedColumn = colState.find((c) => c.sort);

    let sort = sortedColumn
      ? { name: sortedColumn.colId, sort: sortedColumn.sort }
      : null;
    if (filterCallback) {
      filterCallback({
        filter: currentFilters,
        sort: sort,
        pageSize: externalFilter?.pageSize || null,
        defaultFilter: externalFilter?.filter_name || null,
      });
    }
  }, 0);
    timeoutsRef.current.push(timeoutId);
  };

  useEffect(() => {
    if (shouldSetNextPrevIds && Array.isArray(rowData)) {
      let ids = [];
      for (let row of rowData) {
        ids.push(row.id);
      }
      dispatch(setNextPrevIds(ids, exportTitle));
    }
  }, [rowData]);

  let download = (ids) => {
    safeGridCall(() => {
      // SAFE way to get column state
      const colState = columnApi.getColumnState();

      const exportableColumns = colState
        .map((col) => {
          if (!col.hide || col.colId === "id") {
            if (col.colId !== "selection checkbox" && col.colId !== "actions") {
              return col.colId;
            }
          }
          return undefined;
        })
        .filter(Boolean);

      // Now perform the API call normally (safe)
      instance
        .post(
          exportUrl,
          { ids, filter: filterString, sort, fields: exportableColumns,
            defaultFilter: externalFilter?.filter_name || null,
           },
          { responseType: "blob" }
        )
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `${exportTitle}.csv`);
          document.body.appendChild(link);
          link.click();
        });
    });
  };

  useEffect(() => {
    setExportEnabled(selectedIds.length > 0);
  }, [selectedIds]);
  const onSelectionChanged = (event) => {
	const timeoutId = setTimeout(() => {
    let onPageSelected = event.api.getSelectedNodes();
    let alreadySelected = [...selectedIds];

    //Add new ids
    for (let selection of onPageSelected) {
      if (!alreadySelected.some((v) => v === selection.data.id)) {
        alreadySelected.push(selection.data.id);
      }
    }
    //remove unselected ids
    let newSelectedIds = alreadySelected.filter((selection) => {
      let isOnPage = false;

      event.api.forEachNode((row) => {
        if (row.data.id === selection) {
          isOnPage = true;
        }
      });

      if (!isOnPage) {
        return true;
      }
      return onPageSelected.some((v) => v.data.id === selection);
    });
    selectedIds = newSelectedIds;
    setSelectedIds(newSelectedIds);
}, 0);
    timeoutsRef.current.push(timeoutId);
  };

  function buildCurrentFilterCriteria(api, columnApi) {
    if (!api || !columnApi) return null;

    const filterModel = api.getFilterModel();
    const colState = columnApi.getColumnState();

    const sortModel = colState
      .filter((col) => col.sort)
      .sort((a, b) => {
        const ai = a.sortIndex ?? 0;
        const bi = b.sortIndex ?? 0;
        return ai - bi;
      })
      .map((col) => ({
        colId: col.colId,
        sort: col.sort,
      }));

    // const pageSize =
    //   typeof api.paginationGetPageSize === "function"
    //     ? api.paginationGetPageSize()
    //     : 80;

    const columnVisibility = {};
    colState.forEach((col) => {
      // hide: true => visible = false
      columnVisibility[col.colId] = !col.hide;
    });

    return {
      filterModel,
      sortModel,
      columnVisibility,
      //   pageSize,
    };
  }
  const handleSaveFilter = (meta) => {
    if (!gridRef.current) return;

    const api = gridRef.current.api;
    const columnApi = gridRef.current.columnApi;

    const filterCriteria = buildCurrentFilterCriteria(api, columnApi);
    if (!filterCriteria) return;
    // const pageSize = api.paginationGetPageSize();
    if (onSaveFilter) {
      onSaveFilter({
        filter_name: meta.filterName || "filename",
        filter_description: meta.filterDescription || "description",
        is_default: meta.isDefault,
        is_shared: meta.isShared,
        filter_type: filterType,
        // page_size: pageSize,
        filter_criteria: filterCriteria,
      });
    }
  };

  return (
    <div id="table-container" style={{ width: "100%", height: "100%" }}>
      <div className="table-actions-row">
        <Button
          style={{ minWidth: 115 }}
          className={"default-btn-color py-1"}
          onClick={() => download(selectedIds)}
          disabled={!exportEnabled}
        >
          <i className="fa-solid fa-download me-2"></i>
          {`Export ${selectedIds.length > 0 ? selectedIds.length : ""}`}
        </Button>
        <Button
          style={{ minWidth: 115 }}
          className={"default-btn-color py-1"}
          onClick={() => download("all")}
        >
          <i className="fa-solid fa-file-export me-2"></i>
          {"Export All"}
        </Button>
        <Button
          style={{ minWidth: 130, whiteSpace: "nowrap" }}
          className={"default-btn-color py-1"}
          onClick={clearFilter}
        >
          <i className="fa-solid fa-filter-circle-xmark me-2"></i>
          Clear Filters
        </Button>
      </div>
      <Row className="gx-0">
        <Col>
          <div
            id="myGrid"
            style={{
              height: `${tableHeight}`,
              width: "100%",
            }}
            className="ag-theme-alpine"
          >
            <AgGridReact
              ref={gridRef}
              rowData={memoizedRowData}
              // rowData={visibleRowData}
              //   pagination={true}
              //   paginationPageSize={pageSize || 80} // <- control rows per page
              //   suppressPaginationPanel={true}
              onGridReady={onGridReady}
              onRowClicked={rowClick}
              onFilterChanged={onFilterOrSortChanged}
              onSortChanged={onFilterOrSortChanged}
              sortingOrder={["desc", "asc"]}
              suppressRowClickSelection={true}
              rowSelection="multiple"
              columnDefs={columnDefs}
              enableBrowserTooltips={true}
              tooltipShowDelay={300}
              // onSelectionChanged={onSelectionChanged}
              // rowBuffer={externalFilter?.pageSize || 80}
              rowBuffer={20}
              getRowNodeId={(params) => params.row_id}
              onSelectionChanged={onSelectionChanged}
              onColumnVisible={(event) =>
                onColumnVisible(event, setHiddenColumns)
              }
              rowHeight={48}
              suppressRowTransform={true}              
              isExternalFilterPresent={() => true}
              doesExternalFilterPass={() => true}
            />
          </div>
        </Col>
        <Col className="col-auto">
          <SidebarFilters
            colDefs={columnNames}
            grid={gridRef.current}
            filtersFromTable={filters}
            hiddenColumns={hiddenColumns}
            setHiddenColumns={setHiddenColumns}
            barHeight={tableHeight}
            extraFilterButton={extraFilterButton}
            onSaveFilter={handleSaveFilter}
          />
        </Col>
      </Row>
    </div>
  );
}
function createColumnDefs(columns, sort, clickAdd, clickEdit, clickDelete) {
  if (sort) {
    sort = JSON.parse(sort);
  }
  let newColumnDefs = [
    {
      colId: "selection checkbox",
      checkboxSelection: true,
      headerCheckboxSelection: true,
      headerCheckboxSelectionFilteredOnly: true,
      suppressMenu: true,
      width: "55px",
      pinned: "left",
    },
    ...columns.map((column, index) => {
      if (column.field === "actions") {
        return {
          headerName: column.display_name,
          field: column.field,
          cellRendererFramework: actionValueRenderer,
          cellRendererParams: { clickAdd, clickEdit, clickDelete },
          hide: column.hide ?? false,
          suppressMenu: true,
          width: column.width,
          pinned: column.pinned,
          wrapText: true, // for text wrapping
          autoHeight: true, // for text wrapping
        };
      } else if (column.type === "checkbox") {
        return {
          headerName: column.display_name,
          field: column.field,
          hide: column.hide ?? false,
          floatingFilter: false,
          suppressMenu: true,
          cellRendererFramework: checkboxRenderer,
          pinned: column.pinned,
          cellClass: "cell-left-align",
          wrapText: true, // for text wrapping
          autoHeight: true, // for text wrapping
        };
      } else if (column.type === "bool") {
        return {
          headerName: column.display_name,
          field: column.field,
          hide: column.hide ?? false,
          floatingFilter: false,
          filter: column.field === "covering" ? "checkbox" : "agTextColumnFilter",
          floatingFilterComponent:
            floatingFilterComponents[
              column.field === "covering" ? "checkbox" : "text"
            ],
          icon: column.icon ?? "",
          suppressMenu: true,
          width: column.width,
          cellRendererFramework: iconRenderer,
          pinned: column.pinned,
          cellClass: "cell-left-align",
          wrapText: true, // for text wrapping
          autoHeight: true, // for text wrapping
        };
      } else if (column.type === "icon") {
        return {
          headerName: column.display_name,
          field: column.field,
          hide: column.hide ?? false,
          floatingFilter: false,
          filter: column.field === "covering" ? "checkbox" : "agTextColumnFilter",
          floatingFilterComponent:
            floatingFilterComponents[
              column.field === "covering" ? "checkbox" : "text"
            ],
          icon: column.icon ?? "",
          suppressMenu: true,
          width: column.width,
          cellRendererFramework: iconRenderer,
          pinned: column.pinned,
          cellClass: "cell-left-align",
          wrapText: true, // for text wrapping
          autoHeight: true, // for text wrapping
        };
      } else if (column.type === "hyperlink") {
        let redirect_link = column.redirect_link;
        let redirect_value = column.redirect_value;
        return {
          headerName: column.display_name,
          field: column.field,
          suppressMenu: true,
          hide: column.hide ?? false,
          floatingFilter: false,
          cellRendererFramework: hyperlinkRenderer,
          cellRendererParams: { redirect_link, redirect_value },
          pinned: column.pinned,
          wrapText: true, // for text wrapping
          autoHeight: true, // for text wrapping
          cellClass: "cell-left-align",
          cellStyle: {
            "white-space": "pre-wrap",
            "word-wrap": "break-word",
            "line-height": "normal",
            "padding-bottom": "7px",
            "text-align": "left",
          },
        };
      }
         else if (column.type === "priority") {
          // Custom comparator for priority sorting
          const priorityComparator = (valueA, valueB) => {
            const priorityOrder = { 'urgent': 4, 'high': 3, 'medium': 2, 'low': 1 };
            const a = priorityOrder[valueA?.toLowerCase()] || 0;
            const b = priorityOrder[valueB?.toLowerCase()] || 0;
            return a - b;
          };
          return {
            headerName: column.display_name,
            field: column.field,
            hide: column.hide ?? false,
            floatingFilter: false,
            suppressMenu: true,
            cellRendererFramework: PriorityRenderer,
            comparator: priorityComparator,
            sortable: true,
            width: column.width,
            pinned: column.pinned,
            cellClass: "cell-left-align",
            wrapText: true,
            autoHeight: true,
          };
      } else {
        return {
          comparator: comparators[column.type],
          sort: sort && sort.name === column.field ? sort.sort : column.sort,
          sortable: true,
          headerName: column.display_name,
          valueFormatter: valueFormatters[column.type],
          field: column.field,
          filter:
            column.type === "active" || column.type === "checkbox"
              ? "agTextColumnFilter"
              : column.type === "date"
              ? "agTextColumnFilter"
              : column.type === "text" || column.type === "number" || column.type === "dropdown"
              ? "agTextColumnFilter"   // ← dropdown added here
              : column.type,
          floatingFilter: false,
          floatingFilterComponent: floatingFilterComponents[column.type],
          suppressMenu: true,
          floatingFilterComponentParams: {
            suppressFilterButton:
              column.type === "active" ||
              column.type === "checkbox" ||
              column.type === "dropdown",
            dropdownOptions:
              column.type === "dropdown" ? column.dropdownOptions : null,
            dropdownFunction: column.dropdownFunction,
          },
          filterParams: {
            ...filterParams[column.type],
            // buttons:['apply'],
            // debounceMs: 1750
          },
          hide: column.hide ?? false,
          tooltipField: column.field,
          width: column.width,
          pinned: column.pinned,
          cellClass: "cell-left-align",
          wrapText: false, // for text wrapping
          autoHeight: true, // for text wrapping,
          cellStyle: {
            "white-space": "pre-wrap",
            "word-wrap": "break-word",
            "line-height": "normal",
            "padding-bottom": "7px",
            "text-align": "left",
          },
        };
      }
    }),
  ];
  return newColumnDefs;
}
